import { deepFilter, traverse } from 'utils/variableHeight/treeTraversal'
import { Node, RunnerPartialObject } from 'utils/variableHeight/types'
import {
  generatePushTreeForAll,
  getNodesMap,
} from 'utils/variableHeight/generatePushTree'
import {
  heightChanged,
  calculateHeightFromChildren,
} from 'utils/variableHeight/runtime'
import { buildRunnerPartialObject } from 'utils/variableHeight/buildRunnerPartialObject'

const GENERATE_PUSH_TREE_FOR_SCREEN = Symbol('GENERATE_PUSH_TREE_FOR_SCREEN')
const NODE_HEIGHT_CHANGED = Symbol('NODE_HEIGHT_CHANGED')
const ADD_CHILDREN_TO_LIST_NODE = Symbol('ADD_CHILDREN_TO_NODE')

export interface GeneratePushTreePayload {
  body: RunnerPartialObject[]
  deviceType: string | undefined
  screenId: string
}

export interface NodeHeightChangedPayload {
  id: string
  screenId: string
  newHeight: number
}

export interface AddChildrenToNodePayload {
  nodeId: string
  screenId: string
  items: { id: string }[]
  deviceType: string
  prototype: RunnerPartialObject[]
}

export interface Action<ActionType> {
  type: Symbol
  payload: ActionType
}

export type NodesMap = Record<string, Node>
const INITIAL_STATE: Record<string, NodesMap> = {}

export type Payloads =
  | NodeHeightChangedPayload
  | GeneratePushTreePayload
  | AddChildrenToNodePayload

export default (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: Record<string, NodesMap> = INITIAL_STATE,
  action: Action<Payloads>
) => {
  switch (action.type) {
    case GENERATE_PUSH_TREE_FOR_SCREEN: {
      const { body, deviceType, screenId } =
        action.payload as GeneratePushTreePayload
      const pushTree = generatePushTreeMap(body, deviceType, state[screenId])

      const prevState = state[screenId] || {}
      return {
        ...state,
        [screenId]: {
          ...prevState,
          ...pushTree,
        },
      }
    }
    case NODE_HEIGHT_CHANGED: {
      const { id, screenId, newHeight } =
        action.payload as NodeHeightChangedPayload
      const node = state[screenId]?.[id]
      if (node !== undefined) {
        heightChanged(node, newHeight)
      }
      return {
        ...state,
      }
    }
    case ADD_CHILDREN_TO_LIST_NODE: {
      const { nodeId, screenId, prototype, deviceType, items } =
        action.payload as AddChildrenToNodePayload
      const node = state[screenId]?.[nodeId]

      if (!items || !node) {
        return state
      }

      const prevState = state[screenId]
      const pushTree = addChildrenToListNodeHelper(
        node,
        prototype,
        deviceType,
        items,
        prevState
      )

      // generate a new tree for the children
      // append these new children to the node
      return {
        ...state,
        [screenId]: {
          ...prevState,
          ...pushTree,
        },
      }
    }
  }
  return state
}

// reducer helper
export const generatePushTreeMap = (
  body: RunnerPartialObject[],
  deviceType: string | undefined,
  prevState?: Record<string, Node>
): NodesMap => {
  const filteredBody = deepFilter(body, child =>
    removeInvisible(child, deviceType)
  )
  if (prevState) {
    traverse(filteredBody, child => {
      const id = child.listObjId || child.id
      const prevNode = prevState[id]
      if (prevNode) {
        child.newY = prevNode.originalY + prevNode.yOffset
        child.newHeight = prevNode.originalHeight + prevNode.heightOffset
      }
    })
  }

  const pushTree = generatePushTreeForAll(filteredBody)
  return getNodesMap(pushTree)
}

export const addChildrenToListNodeHelper = (
  node: Node,
  prototype: RunnerPartialObject[],
  deviceType: string,
  items: { id: string }[],
  prevState: NodesMap
): NodesMap => {
  const listItems: RunnerPartialObject[] = items.map((item, index) =>
    buildRunnerPartialObject({
      id: getCompositeId(node.objectId, item.id),
      type: 'LIST_ITEM',
      itemId: item.id,
      name: `${node.name} - item ${index}`,
      height: 0,
      width: 0,
      x: 0,
      adjustedY: 0,
    })
  )

  listItems.forEach((listItem, index) => {
    listItem.children = prototype.map(protoObj => ({
      ...protoObj,
      id: getCompositeId(protoObj.id, listItem.itemId),
      attributes: {
        ...protoObj.attributes,
        name: `${protoObj.attributes.name} - item ${index}`,
      },
    }))
  })

  const pushTree = generatePushTreeMap(listItems, deviceType, prevState)
  node.children = listItems.map(listItem => pushTree[listItem.id])

  node.children.forEach(child => {
    calculateHeightFromChildren(child)
  })

  return pushTree
}

export const getCompositeId = (id1: string, id2?: string): string =>
  `${id1} - ${id2}`

// actions
export const generatePushTreeForScreen = ({
  body,
  deviceType,
  screenId,
}: GeneratePushTreePayload): Action<GeneratePushTreePayload> => ({
  type: GENERATE_PUSH_TREE_FOR_SCREEN,
  payload: {
    body,
    deviceType,
    screenId,
  },
})

export const addChildrenToListNode = ({
  nodeId,
  screenId,
  prototype,
  items,
  deviceType,
}: AddChildrenToNodePayload): Action<AddChildrenToNodePayload> => ({
  type: ADD_CHILDREN_TO_LIST_NODE,
  payload: {
    nodeId,
    screenId,
    prototype,
    items,
    deviceType,
  },
})

export const nodeHeightChanged = ({
  id,
  screenId,
  newHeight,
}: NodeHeightChangedPayload): Action<NodeHeightChangedPayload> => ({
  type: NODE_HEIGHT_CHANGED,
  payload: {
    id,
    screenId,
    newHeight,
  },
})

// selectors
export const getNodeY = (
  state: { pushTreeMap: Record<string, NodesMap> },
  componentId: string,
  id: string
) => {
  const obj = state.pushTreeMap[componentId]?.[id]
  if (!obj) {
    return 0
  }
  return obj.originalY + obj.yOffset
}

export const getNodeHeight = (
  state: { pushTreeMap: Record<string, NodesMap> },
  componentId: string,
  id: string
) => {
  const obj = state.pushTreeMap[componentId]?.[id]
  if (!obj) {
    return undefined
  }
  return obj.originalHeight + obj.heightOffset
}

const removeInvisible = (
  object: RunnerPartialObject,
  deviceType: string | undefined
) => {
  const { attributes } = object
  const { deviceVisibility } = attributes
  if (deviceVisibility && deviceType) {
    return deviceVisibility[deviceType]
  }
  return true
}
